"use client";

import React from "react";
import { ArrowDown, Ticket, Handshake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CinematicGardenEntrance() {
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll();
  const buyTicketsLink = "/tickets";
  const sponsorLink = "/sponsors";
  return <section id="cinematic-garden-entrance" className="relative h-screen w-full overflow-hidden dark bg-background text-foreground">
      {/* Video Background */}
      <div className="absolute inset-0 z-0 bg-black">
        <video autoPlay loop muted playsInline className="object-cover opacity-90 w-full h-full" style={{
        objectFit: "cover",
        minHeight: "100%",
        minWidth: "100%"
      }}>
          <source src="/videos/sabor_hero_loop.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Overlay Gradients */}
      <div className="absolute inset-0 z-10 bg-[hsl(220,20%,10%)]/70 mix-blend-multiply pointer-events-none" />
      <div className="absolute inset-0 z-10 bg-black/40 pointer-events-none" />

      {/* Content */}
      <div className="relative z-20 flex h-full flex-col items-center justify-center px-4 text-center sm:px-6 lg:px-8">
        <div ref={contentRef} className={`flex max-w-5xl flex-col items-center space-y-8 transition-all duration-1000 ease-out ${isContentVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {/* Headline */}
          <h1 className="font-serif text-5xl font-bold leading-tight tracking-tight sm:text-6xl md:text-7xl lg:text-8xl">
            <span className="bg-gradient-to-b from-[var(--color-primary)] to-[var(--color-foreground)] bg-clip-text text-transparent drop-shadow-2xl">
              SABOR: THE MULTICULTURAL PINNACLE
            </span>
          </h1>

          {/* Subheadline */}
          <p className="max-w-2xl text-lg font-light text-gray-200 sm:text-xl md:text-2xl">
            Experience the intersection of culinary excellence, corporate
            leadership, and cultural vibrancy at the Denver Botanic Gardens.
          </p>

          {/* Call to Action Buttons */}
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 pt-8">
            <Button asChild size="lg" className="h-14 min-w-[200px] bg-[var(--color-primary)] text-[var(--color-primary-foreground)] text-lg font-semibold shadow-[0_0_20px_rgba(234,179,8,0.3)] hover:bg-[var(--color-primary)]/90 hover:shadow-[0_0_30px_rgba(234,179,8,0.5)] transition-all duration-300">
              <Link to={buyTicketsLink}>
                <Ticket className="mr-2 h-5 w-5" />
                Buy Tickets
              </Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="h-14 min-w-[200px] border-2 border-[var(--color-chart-2)] bg-transparent text-[var(--color-chart-2)] text-lg font-semibold backdrop-blur-sm hover:bg-[var(--color-chart-2)] hover:text-white hover:border-[var(--color-chart-2)] transition-all duration-300">
              <Link to={sponsorLink}>
                <Handshake className="mr-2 h-5 w-5" />
                Become a Sponsor
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 transform">
        <div className="flex flex-col items-center gap-2 opacity-80">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--color-primary)]">
            Scroll to Explore
          </span>
          <div className="relative h-16 w-[1px] overflow-hidden bg-white/20">
            <div className="absolute top-0 left-0 h-1/2 w-full animate-[drop_2s_infinite] bg-[var(--color-primary)]" />
          </div>
        </div>
      </div>

      {/* Animation Keyframes */}
      <style jsx global>{`
        @keyframes drop {
          0% {
            top: -50%;
          }
          100% {
            top: 100%;
          }
        }
      `}</style>
    </section>;
}